import { IconLoader } from 'ui';
import { useState, ReactNode, CSSProperties, KeyboardEvent } from 'react';
import { useTranslations } from 'i18n';
import styles from './cordion.module.css';

type CordionProps = {
    headerElement: ReactNode;
    contentElement: ReactNode;
    id?: string;
    wrapperStyles?: CSSProperties;
    headerWrapperStyles?: CSSProperties;
    iconWrapperStyles?: CSSProperties;
    contentWrapperStyles?: CSSProperties;
    trigger?: 'icon' | 'header';
    variant?: 'right' | 'left';
    isOpen?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    /** Accessible, row-specific name for the icon trigger (and the region it discloses). Used when trigger="icon". */
    triggerLabel?: string;
};

/**
 * co(ntrolled acco)rdion component
 * optional onOpen function
 * optional onClose function
 * optional style props for wrapper, header, content containers
 * trigger options: icon or whole header
 * variant: right or left
 */
export default function ControlledAccordion(props: CordionProps) {
    const {
        headerElement,
        contentElement,
        id,
        wrapperStyles,
        headerWrapperStyles,
        iconWrapperStyles,
        contentWrapperStyles,
        trigger = 'icon',
        variant = 'right',
        isOpen = false,
        onOpen,
        onClose,
        triggerLabel,
    } = props;
    const [open, setOpen] = useState(isOpen);
    const { __ } = useTranslations();

    const handler = () => {
        if (!open && onOpen) {
            onOpen();
        } else if (open && onClose) {
            onClose();
        }
        setOpen(!open);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            handler();
        }
    };

    const isHeaderTrigger = trigger === 'header';
    const isIconTrigger = trigger === 'icon';

    const headerId = id ? `${id}-id-header` : undefined;
    const panelId = id ? `${id}-id-panel` : undefined;

    return (
        <div className={`${styles['cordion-wrapper']}`} style={wrapperStyles}>
            <div
                className={`${styles['header-wrapper']}`}
                style={{ flexDirection: variant === 'left' ? 'row-reverse' : 'row', ...headerWrapperStyles }}
                onClick={isHeaderTrigger ? handler : undefined}
                role={isHeaderTrigger ? 'button' : undefined}
                tabIndex={isHeaderTrigger ? 0 : undefined}
                onKeyDown={isHeaderTrigger ? handleKeyDown : undefined}
                id={isHeaderTrigger ? headerId : undefined}
                aria-controls={isHeaderTrigger ? panelId : undefined}
                aria-expanded={isHeaderTrigger ? open : undefined}
            >
                {headerElement}
                {isIconTrigger ? (
                    <button
                        type="button"
                        className={styles['icon-trigger']}
                        style={iconWrapperStyles}
                        onClick={handler}
                        aria-label={triggerLabel ?? __('misc.toggle_details')}
                        id={headerId}
                        aria-controls={panelId}
                        aria-expanded={open}
                    >
                        <IconLoader
                            iconName="IconChevronDown"
                            size="sm"
                            className={`${styles['rotating-icon']} ${open ? styles['is-open'] : ''}`}
                        />
                    </button>
                ) : (
                    <div style={iconWrapperStyles}>
                        <IconLoader
                            iconName="IconChevronDown"
                            size="sm"
                            className={`${styles['rotating-icon']} ${open ? styles['is-open'] : ''}`}
                        />
                    </div>
                )}
            </div>
            {open && (
                <div
                    className={`${styles['content-wrapper']}`}
                    style={contentWrapperStyles}
                    id={panelId}
                    role="region"
                    aria-labelledby={headerId}
                >
                    {contentElement}
                </div>
            )}
        </div>
    );
}
